import { useEffect, useMemo, useState } from 'react';

import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Combobox } from '@apps/web/components/ui/combobox';
import { CommandEmpty, CommandItem } from '@apps/web/components/ui/command';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { cn } from '@apps/web/lib/utils';
import {
    useWellSkyCaregiverRateInfo,
    useWellSkyClientRateInfo,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { ApiError, type RateCategory } from '@libraries/typescript/api_client';

/**
 * The backend returns HTTP 404 with this shape when WellSky's
 * rate-info endpoint 404s on the stored external_shift_id. Several
 * underlying causes are possible (shift edited, given a new id,
 * deleted client, auth leak, endpoint change, vendor flake), so the
 * UI does NOT assert a single cause — it surfaces a refresh-focused
 * message and leaves the interpretation to the user.
 *
 *   { detail: { code: 'wellsky_rate_info_not_found', message: string, ... } }
 */
function isWellSkyRateInfoNotFound(error: unknown): boolean {
    if (!(error instanceof ApiError)) {
        return false;
    }
    if (error.status !== 404) {
        return false;
    }
    const detail = (error.body as { detail?: { code?: string } } | undefined)
        ?.detail;
    return detail?.code === 'wellsky_rate_info_not_found';
}

export function getRateCategoryDisplayName(rateCategory: RateCategory): string {
    return rateCategory.display_name ?? rateCategory.name;
}

const CUSTOM_CAREGIVER_RATE_AMOUNT_PATTERN =
    /^(?:0|[1-9]\d{0,3})(?:\.\d{1,2})?$/;
const MAX_CUSTOM_CAREGIVER_RATE_AMOUNT = 9999.99;

const CUSTOM_CAREGIVER_RATE_TYPES = [
    { value: '1', label: 'Hourly' },
    { value: '2', label: 'Per Visit' },
    { value: '3', label: 'Live In' },
] as const;

type CustomCaregiverRateType =
    (typeof CUSTOM_CAREGIVER_RATE_TYPES)[number]['value'];

export function isValidCustomCaregiverRateAmount(
    amount: string | null | undefined,
): boolean {
    const trimmed = amount?.trim();
    if (!trimmed || !CUSTOM_CAREGIVER_RATE_AMOUNT_PATTERN.test(trimmed)) {
        return false;
    }

    const parsed = Number(trimmed);
    return parsed > 0 && parsed <= MAX_CUSTOM_CAREGIVER_RATE_AMOUNT;
}

function toCustomCaregiverRateType(
    value: number | string | null | undefined,
): CustomCaregiverRateType | null {
    const rateType = String(value ?? '');
    return CUSTOM_CAREGIVER_RATE_TYPES.some(
        (option) => option.value === rateType,
    )
        ? (rateType as CustomCaregiverRateType)
        : null;
}

export type ShiftRateState = {
    readonly clientRateId: string | null;
    readonly clientRate: RateCategory | null;
    readonly caregiverRateId: string | null;
    readonly caregiverRate: RateCategory | null;
    readonly caregiverRateCustomType: string | null;
    readonly caregiverRateCustomAmount: string | null;
};

// A custom caregiver rate is only valid when BOTH the rate type and a valid
// amount are present. Single source of truth so the submit guard and the
// payload builder cannot disagree on what "has a custom rate" means.
export function hasValidCustomCaregiverRate(
    rateState:
        | Pick<
              ShiftRateState,
              'caregiverRateCustomType' | 'caregiverRateCustomAmount'
          >
        | null
        | undefined,
): boolean {
    return (
        Boolean(rateState?.caregiverRateCustomType) &&
        isValidCustomCaregiverRateAmount(rateState?.caregiverRateCustomAmount)
    );
}

type ShiftRateSelectorProps = {
    readonly organizationId: string;
    readonly outreachId: string;
    readonly shiftId: string;
    readonly caregiverId: string;
    readonly disabled?: boolean;
    readonly onRateChange: (shiftId: string, state: ShiftRateState) => void;
    readonly hasClientRateError?: boolean;
    readonly hasCaregiverRateError?: boolean;
};

export function ShiftRateSelector({
    organizationId,
    outreachId,
    shiftId,
    caregiverId,
    disabled = false,
    onRateChange,
    hasClientRateError = false,
    hasCaregiverRateError = false,
}: ShiftRateSelectorProps) {
    const [selectedClientRateId, setSelectedClientRateId] = useState<
        string | null
    >(null);
    const [isClientRateDropdownOpen, setIsClientRateDropdownOpen] =
        useState(false);
    const [clientRateSearch, setClientRateSearch] = useState('');

    const [selectedCaregiverRateId, setSelectedCaregiverRateId] = useState<
        string | null
    >(null);
    const [isCaregiverRateDropdownOpen, setIsCaregiverRateDropdownOpen] =
        useState(false);
    const [caregiverRateSearch, setCaregiverRateSearch] = useState('');
    const [useCustomCaregiverRate, setUseCustomCaregiverRate] = useState(false);
    const [customCaregiverRateType, setCustomCaregiverRateType] =
        useState<CustomCaregiverRateType>('1');
    const [customCaregiverRateAmount, setCustomCaregiverRateAmount] =
        useState('');

    // Fetch client rate info for this specific shift
    const {
        data: clientRateInfo,
        isLoading: isClientRateLoading,
        error: clientRateInfoError,
    } = useWellSkyClientRateInfo(
        organizationId,
        outreachId,
        { reason: 'ShiftRateSelector' },
        shiftId,
    );

    const clientRateOptions = useMemo(
        () => clientRateInfo?.rate_categories ?? [],
        [clientRateInfo],
    );

    const selectedClientRate = useMemo(() => {
        if (!selectedClientRateId) {
            return null;
        }
        return (
            clientRateOptions.find(
                (category) => String(category.id) === selectedClientRateId,
            ) ?? null
        );
    }, [selectedClientRateId, clientRateOptions]);

    // Fetch caregiver rate info for this specific shift
    const {
        data: caregiverRateInfo,
        isLoading: isCaregiverRateLoading,
        error: caregiverRateInfoError,
    } = useWellSkyCaregiverRateInfo(
        organizationId,
        outreachId,
        selectedClientRateId,
        caregiverId,
        { reason: 'ShiftRateSelector' },
        shiftId,
    );

    const caregiverRateOptions = useMemo(
        () => caregiverRateInfo?.rate_categories ?? [],
        [caregiverRateInfo],
    );

    const selectedCaregiverRate = useMemo(() => {
        if (useCustomCaregiverRate || !selectedCaregiverRateId) {
            return null;
        }
        return (
            caregiverRateOptions.find(
                (category) => String(category.id) === selectedCaregiverRateId,
            ) ?? null
        );
    }, [useCustomCaregiverRate, selectedCaregiverRateId, caregiverRateOptions]);

    const defaultCustomCaregiverRateType =
        toCustomCaregiverRateType(
            caregiverRateOptions.find(
                (category) => String(category.id) === selectedCaregiverRateId,
            )?.type,
        ) ??
        toCustomCaregiverRateType(
            caregiverRateInfo?.default_rate_category?.type,
        ) ??
        '1';
    const canUseCustomCaregiverRate =
        Boolean(selectedClientRateId) &&
        !isCaregiverRateLoading &&
        !caregiverRateInfoError &&
        Boolean(caregiverRateInfo);
    const isCustomCaregiverRateControlDisabled =
        disabled || !canUseCustomCaregiverRate;
    // Never disable the checkbox while it is already checked — a client-rate
    // change (which refetches caregiver rates) or a fetch error would otherwise
    // trap the user with a checked box they cannot turn off.
    const isCustomCaregiverRateCheckboxDisabled =
        disabled || (!canUseCustomCaregiverRate && !useCustomCaregiverRate);
    const isReportingCustomCaregiverRate =
        useCustomCaregiverRate && canUseCustomCaregiverRate;

    // WellSky keys caregiver-rate options by the selected client rate, so a
    // client-rate change invalidates any previously-picked caregiver rate.
    useEffect(() => {
        setSelectedCaregiverRateId(null);
        setCaregiverRateSearch('');
    }, [selectedClientRateId]);

    // Auto-select default client rate when data loads
    useEffect(() => {
        if (
            clientRateInfo?.default_rate_category &&
            !selectedClientRateId &&
            clientRateOptions.length > 0
        ) {
            setSelectedClientRateId(
                String(clientRateInfo.default_rate_category.id),
            );
        }
    }, [clientRateInfo, selectedClientRateId, clientRateOptions.length]);

    // Auto-select default caregiver rate when client rate is selected and data loads
    useEffect(() => {
        if (
            !useCustomCaregiverRate &&
            caregiverRateInfo?.default_rate_category &&
            !selectedCaregiverRateId &&
            caregiverRateOptions.length > 0
        ) {
            const defaultRateCategory = caregiverRateInfo.default_rate_category;
            setSelectedCaregiverRateId(String(defaultRateCategory.id));
            setCustomCaregiverRateType(
                toCustomCaregiverRateType(defaultRateCategory.type) ?? '1',
            );
        }
    }, [
        useCustomCaregiverRate,
        caregiverRateInfo,
        selectedCaregiverRateId,
        caregiverRateOptions.length,
    ]);

    // Report rate changes to parent
    useEffect(() => {
        onRateChange(shiftId, {
            clientRateId: selectedClientRateId,
            clientRate: selectedClientRate,
            caregiverRateId: selectedCaregiverRateId,
            caregiverRate: selectedCaregiverRate,
            caregiverRateCustomType: isReportingCustomCaregiverRate
                ? customCaregiverRateType
                : null,
            caregiverRateCustomAmount: isReportingCustomCaregiverRate
                ? customCaregiverRateAmount.trim() || null
                : null,
        });
    }, [
        shiftId,
        selectedClientRateId,
        selectedClientRate,
        selectedCaregiverRateId,
        selectedCaregiverRate,
        isReportingCustomCaregiverRate,
        customCaregiverRateType,
        customCaregiverRateAmount,
        onRateChange,
    ]);

    const clientRateComboboxDisplay = selectedClientRate
        ? getRateCategoryDisplayName(selectedClientRate)
        : undefined;

    const caregiverRateComboboxDisplay = selectedCaregiverRate
        ? getRateCategoryDisplayName(selectedCaregiverRate)
        : undefined;

    return (
        <div className="mt-2 flex items-start gap-3 pl-6">
            <div className="flex min-w-0 flex-1 flex-col gap-1 text-xs">
                <Label className="text-muted-foreground text-xs font-medium">
                    Client Rate
                </Label>
                <Combobox
                    open={isClientRateDropdownOpen}
                    onOpenChange={setIsClientRateDropdownOpen}
                    displayValue={clientRateComboboxDisplay}
                    placeholder={
                        isClientRateLoading
                            ? 'Loading rates...'
                            : isWellSkyRateInfoNotFound(clientRateInfoError)
                              ? 'Linked shift may have been removed or edited in WellSky — refresh'
                              : clientRateInfoError
                                ? 'Error loading rates'
                                : 'Select client rate'
                    }
                    disabled={
                        disabled ||
                        isClientRateLoading ||
                        clientRateOptions.length === 0
                    }
                    searchValue={clientRateSearch}
                    onSearchValueChange={setClientRateSearch}
                    searchPlaceholder="Search client rates..."
                    triggerClassName={cn(
                        'text-left overflow-hidden',
                        hasClientRateError &&
                            'border-destructive focus-visible:ring-destructive',
                    )}
                    contentClassName="w-72 p-1"
                    contentSide="bottom"
                    contentSideOffset={8}
                    commandListProps={{
                        className: 'max-h-64',
                    }}
                >
                    <CommandEmpty className="text-muted-foreground py-6 text-center text-xs">
                        No client rates found
                    </CommandEmpty>
                    {clientRateOptions.map((rateCategory) => (
                        <CommandItem
                            key={rateCategory.id}
                            value={getRateCategoryDisplayName(rateCategory)}
                            onSelect={() => {
                                setSelectedClientRateId(
                                    String(rateCategory.id),
                                );
                                setIsClientRateDropdownOpen(false);
                            }}
                            className="py-2 text-left text-sm"
                        >
                            {getRateCategoryDisplayName(rateCategory)}
                        </CommandItem>
                    ))}
                </Combobox>
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2 text-xs">
                <div className="flex items-center justify-between gap-2">
                    <Label className="text-muted-foreground text-xs font-medium">
                        Caregiver Rate
                    </Label>
                    <Label
                        htmlFor={`custom-caregiver-rate-${shiftId}`}
                        className="text-muted-foreground flex items-center gap-1.5 text-xs font-normal"
                    >
                        <Checkbox
                            id={`custom-caregiver-rate-${shiftId}`}
                            checked={useCustomCaregiverRate}
                            disabled={isCustomCaregiverRateCheckboxDisabled}
                            onCheckedChange={(checked) => {
                                const isChecked = checked === true;
                                setUseCustomCaregiverRate(isChecked);
                                if (isChecked) {
                                    setCustomCaregiverRateType(
                                        defaultCustomCaregiverRateType,
                                    );
                                    setSelectedCaregiverRateId(null);
                                } else {
                                    setCustomCaregiverRateAmount('');
                                }
                            }}
                        />
                        Custom amount
                    </Label>
                </div>
                <Combobox
                    open={isCaregiverRateDropdownOpen}
                    onOpenChange={setIsCaregiverRateDropdownOpen}
                    displayValue={caregiverRateComboboxDisplay}
                    placeholder={
                        isWellSkyRateInfoNotFound(clientRateInfoError)
                            ? 'Linked shift may have been removed or edited in WellSky — refresh'
                            : !selectedClientRateId
                              ? 'Select client rate first'
                              : isCaregiverRateLoading
                                ? 'Loading rates...'
                                : caregiverRateInfoError
                                  ? 'Error loading rates'
                                  : 'Select caregiver rate'
                    }
                    disabled={
                        disabled ||
                        !selectedClientRateId ||
                        useCustomCaregiverRate ||
                        isCaregiverRateLoading ||
                        caregiverRateOptions.length === 0
                    }
                    searchValue={caregiverRateSearch}
                    onSearchValueChange={setCaregiverRateSearch}
                    searchPlaceholder="Search caregiver rates..."
                    triggerClassName={cn(
                        'text-left overflow-hidden',
                        hasCaregiverRateError &&
                            'border-destructive focus-visible:ring-destructive',
                    )}
                    contentClassName="w-72 p-1"
                    contentSide="bottom"
                    contentSideOffset={8}
                    commandListProps={{
                        className: 'max-h-64',
                    }}
                >
                    <CommandEmpty className="text-muted-foreground py-6 text-center text-xs">
                        No caregiver rates found
                    </CommandEmpty>
                    {caregiverRateOptions.map((rateCategory) => (
                        <CommandItem
                            key={rateCategory.id}
                            value={getRateCategoryDisplayName(rateCategory)}
                            onSelect={() => {
                                setSelectedCaregiverRateId(
                                    String(rateCategory.id),
                                );
                                setCustomCaregiverRateType(
                                    toCustomCaregiverRateType(
                                        rateCategory.type,
                                    ) ?? '1',
                                );
                                setIsCaregiverRateDropdownOpen(false);
                            }}
                            className="py-2 text-left text-sm"
                        >
                            {getRateCategoryDisplayName(rateCategory)}
                        </CommandItem>
                    ))}
                </Combobox>
                {useCustomCaregiverRate && (
                    <div className="grid grid-cols-[minmax(6.5rem,0.85fr)_minmax(7rem,1fr)] gap-2">
                        <Select
                            value={customCaregiverRateType}
                            onValueChange={(value) => {
                                const rateType =
                                    toCustomCaregiverRateType(value);
                                if (rateType) {
                                    setCustomCaregiverRateType(rateType);
                                }
                            }}
                            disabled={isCustomCaregiverRateControlDisabled}
                        >
                            <SelectTrigger
                                size="sm"
                                className="h-8 w-full text-xs"
                                aria-label="Custom caregiver pay rate type"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CUSTOM_CAREGIVER_RATE_TYPES.map((option) => (
                                    <SelectItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Input
                            type="text"
                            inputMode="decimal"
                            value={customCaregiverRateAmount}
                            onChange={(event) => {
                                setCustomCaregiverRateAmount(
                                    event.target.value,
                                );
                            }}
                            placeholder="Custom pay rate"
                            aria-label="Custom caregiver pay rate"
                            disabled={isCustomCaregiverRateControlDisabled}
                            aria-invalid={
                                hasCaregiverRateError &&
                                !isValidCustomCaregiverRateAmount(
                                    customCaregiverRateAmount,
                                )
                            }
                            className="h-8 text-sm"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
